/* eslint-disable react/no-array-index-key */

import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Dump from 'components/Dump';

import { AiRoleEnum } from 'data/aiConversations/aiConversationsTypes';

import { avatarSxChat, getAppIcon } from '../aiConfig';
import AssistantResponse from './AssistantResponse';
import ChatFeedback from './ChatFeedback';
import ManageMemories from './ManageMemories';
import TextWithFeatures from './TextWithFeatures';

// STYLES ------------------------------------------------------------------------
const useStyles = makeStyles()((theme) => ({
  itemContainer: {
    flexGrow: 1,
    minHeight: 320,
    overflowY: 'auto',
    marginBottom: '16px',
  },
  replySkeleton: {
    backgroundColor: theme.palette.skeleton.light,
  },
}));

//------------------------------------------------------------------------
// ChatDisplay Component
//------------------------------------------------------------------------
export default function ChatDisplay({
  messages,
  setAutoConversation,
  submitAutoPrompt,
  isProcessing,
  onFeedbackSubmit,
  displayName,
  responseIsJson,
}) {
  const scrollRef = useRef(null);
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const [feedbackOnLastMessage, setFeedbackOnLastMessage] = useState(0);
  const [showManageMemories, setShowManageMemories] = useState(false);

  // Smooth scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      // Create a new 'div' to act as a scroll target
      const scrollTarget = document.createElement('div');
      scrollRef.current.appendChild(scrollTarget);
      // Scroll into view smoothly
      scrollTarget.scrollIntoView({ behavior: 'smooth' });
      // Clean up by removing the scroll target after scroll
      return () => {
        if (scrollTarget.parentNode) {
          scrollTarget.parentNode.removeChild(scrollTarget);
        }
      };
    }
    return undefined;
  }, [messages, scrollRef, feedbackOnLastMessage]); // Trigger scrolling when messages change or feedback expands

  const processFeedbackSubmit = (feedback) => {
    if (feedback.messageIndex === messages.size - 1) {
      setFeedbackOnLastMessage(feedbackOnLastMessage + 1);
    }
    onFeedbackSubmit(feedback);
  };
  const AppIcon = getAppIcon(theme);

  return (
    <Box sx={{ paddingX: 3, paddingY: 2 }} className={classes.itemContainer}>
      {/* ===== Render the Manage Memories Dialog ===== */}

      <ManageMemories open={showManageMemories} onClose={() => setShowManageMemories(false)} />

      <Box
        sx={{ margin: 'auto', flexGrow: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}
        ref={scrollRef}
      >
        {/* ===== Render the chat messages ===== */}
        {messages.map((message, index) => (
          <Stack key={`stk1${index}`} gap={1} marginY={2}>
            {message.role !== AiRoleEnum.USER && (
              <Stack direction='row' gap={1} alignItems='center'>
                <Avatar src={AppIcon} sx={avatarSxChat} />
                <Typography sx={{ marginTop: '-10px' }} variant='bodyMedium' component='span'>
                  {displayName}
                </Typography>
              </Stack>
            )}
            <Dump value={message} id='message' />
            <Box
              id='message'
              key={`message${index}`}
              sx={{
                marginLeft: message.role === AiRoleEnum.USER ? 0 : 1,
                padding: message.role === AiRoleEnum.USER ? 1 : 0,
                borderRadius: '12px',
                maxWidth: message.role === AiRoleEnum.USER ? '580px' : null,
                width: message.role === AiRoleEnum.USER ? 'auto' : '100%',
                alignSelf: message.role === AiRoleEnum.USER ? 'flex-end' : 'flex-start',
                backgroundColor: message.role === AiRoleEnum.USER ? theme.palette.background.default : undefined,
              }}
            >
              {message.role === AiRoleEnum.USER && (
                <TextWithFeatures
                  text={message.message}
                  followUpFunction={(text) => (submitAutoPrompt ? submitAutoPrompt(text) : undefined)}
                />
              )}

              {message.role === AiRoleEnum.ASSISTANT && (
                <React.Fragment key={`AssistantResponseFrag${index}${message.message.length}`}>
                  <AssistantResponse
                    message={message}
                    submitAutoPrompt={submitAutoPrompt}
                    responseIsJson={responseIsJson}
                    onManageMemories={() => setShowManageMemories(true)}
                  />

                  {!isProcessing && (
                    <ChatFeedback
                      messageIndex={index}
                      messages={messages}
                      message={message}
                      setAutoConversation={setAutoConversation}
                      onFeedbackSubmit={processFeedbackSubmit}
                      onManageMemories={() => setShowManageMemories(true)}
                    />
                  )}
                </React.Fragment>
              )}

              {isProcessing && index === messages.size - 1 && (
                <React.Fragment key={`skeletonSection-${index}`}>
                  <Skeleton animation='wave' className={classes.replySkeleton} />
                  <Skeleton animation='wave' width='75%' className={classes.replySkeleton} />
                  <Skeleton animation='wave' width='50%' className={classes.replySkeleton} />
                </React.Fragment>
              )}
            </Box>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}

ChatDisplay.propTypes = {
  onFeedbackSubmit: PropTypes.func,
  messages: PropTypes.any.isRequired,
  setAutoConversation: PropTypes.func,
  submitAutoPrompt: PropTypes.func,
  isProcessing: PropTypes.bool,
  displayName: PropTypes.string,
  responseIsJson: PropTypes.bool,
};
